import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { catchError, finalize, map, Observable, of, share, tap } from 'rxjs';
import { AuthApiService } from '../auth-api/auth-api.service';
import {
  SessionCheckFailedError,
  UnauthorizedRequest,
} from '../auth-api/session-errors';
import { withExpectedUnauthorized } from '../auth-interceptor/auth-context';

export interface SessionProbeResult {
  readonly state: 'alive' | 'dead' | 'unknown';
  /** 0 when the request never reached the API. */
  readonly meStatus: number;
}

function classify(error: unknown): SessionProbeResult {
  const status = error instanceof HttpErrorResponse ? error.status : 0;
  return {
    state: status === HttpStatusCode.Unauthorized ? 'dead' : 'unknown',
    meStatus: status,
  };
}

/**
 * Asks `/me` whether the session cookie is still valid. Requests that fail
 * together share one probe, so a page firing several requests at once costs a
 * single `/me` and, when `/me` cannot answer, a single report naming the
 * request that started it.
 */
@Injectable({ providedIn: 'root' })
export class SessionProbeService {
  readonly #authApi = inject(AuthApiService);
  readonly #errorHandler = inject(ErrorHandler);
  #inFlight: Observable<SessionProbeResult> | null = null;

  probe(trigger: UnauthorizedRequest): Observable<SessionProbeResult> {
    this.#inFlight ??= this.#authApi
      .getUserData(withExpectedUnauthorized())
      .pipe(
        map((): SessionProbeResult => ({
          state: 'alive',
          meStatus: HttpStatusCode.Ok,
        })),
        catchError((error: unknown) => of(classify(error))),
        tap(({ state, meStatus }) => {
          if (state === 'unknown') {
            this.#errorHandler.handleError(
              new SessionCheckFailedError(1, meStatus, trigger),
            );
          }
        }),
        finalize(() => {
          this.#inFlight = null;
        }),
        share(),
      );
    return this.#inFlight;
  }
}
